import { h } from 'preact';

import { Translate } from '@travel/translation';
import ItemFeatures from '@travel/traveler-core/components/ItemFeatures';
import { useDeviceTypes, useDialogHandler } from '@travel/traveler-core/hooks';
import { parseSearchQuery, URL_KEYS } from '@travel/traveler-core/utils/jrdpHelper';
import { cx, stringify } from '@travel/utils';

import ImageGallery from 'components/ImageGallery';
import PriceInfo from 'components/PriceInfo';
import TextBadge from 'components/TextBadge';
import VacancyCalendarEntryButton from 'components/VacancyCalendar/VacancyCalendarEntryButton';
import RoomDetailPopUp from '../../../PlansList/components/RoomDetailPopup';

import { HasMeal, Station, URLSearchQuery } from 'Common-Types';
import paths from 'constants/paths';
import { Item } from 'PlanList-Types';

import styles from './roomInfo.module.scss';

export type Props = {
  query?: URLSearchQuery;
  /* Object of current item data */
  item: Item;
  /** String of plan id that is belong to room item */
  planId: string;
  /** String of hotel id that is belong to room item */
  hotelId: string;
  /** Departure station that is belong to room item */
  departureStation?: Station;
  /** Destination station that is belong to room item */
  destinationStation?: Station;
  /** String for accommodation area path for navigation */
  accommodationAreaPath: string;
  /** String for accommodations area path for navigation */
  accommodationsPath: string;
  /** String for departure area code for navigation and Vacancy Calendar search */
  departureAreaCode: string;
  /** HasMeal Information for Vacancy Calendar search */
  hasMeal?: HasMeal;
};

const RoomInfo = (props: Props) => {
  const {
    item,
    planId,
    hotelId,
    query,
    departureStation,
    destinationStation,
    accommodationAreaPath,
    accommodationsPath,
    departureAreaCode,
    hasMeal,
  } = props;

  const {
    roomSize,
    capacity,
    itemId,
    media,
    points,
    price,
    name: roomName,
    description,
    inventoryCount,
    featureGroups,
  } = item;
  const { adultCount, childCount, noOfDays, stayNights, noOfUnits } = parseSearchQuery(query);

  const {
    isOpen: isRoomDetailOpen,
    onOpen: onRoomDetailOpen,
    onClose: onRoomDetailClose,
  } = useDialogHandler(false);

  const { isPC } = useDeviceTypes();
  const trainSearchUrl = `${paths.trainSearch.path}?${stringify({
    ...props.query,
    [URL_KEYS.DEPARTURE]: departureAreaCode,
    [URL_KEYS.ACCOMMODATION_AREA]: accommodationAreaPath,
    [URL_KEYS.PLACE_OF_STAY]: accommodationsPath,
    [URL_KEYS.DEPARTURE_STATION]: departureStation?.code,
    [URL_KEYS.ARRIVAL_STATION]: destinationStation?.code,
    [URL_KEYS.PROVIDER_ID]: hotelId,
    [URL_KEYS.ITEM_ID]: itemId,
    [URL_KEYS.PLAN_ID]: planId,
    [URL_KEYS.TRANSACTION_ID]: undefined,
  })}`;

  return (
    <div className={styles.roomContainer} data-testid="roomInfo-wrapper">
      {isPC ? (
        <ImageGallery
          media={media}
          galleryType="thumbnail-slide"
          className={styles.imageGallery}
          swiperClassName={styles.imageGallery}
          thumbnailClass={styles.galleryThumbnail}
          galleryClassName={styles.imageWrapper}
        />
      ) : (
        <ImageGallery
          media={media}
          galleryType="scroll"
          mediaClassName={styles.mediaSP}
          galleryClassName={styles.imageWrapper}
        />
      )}
      <div className={cx(styles.roomInfoWrapper, styles.contentBox)}>
        {roomName && (
          <button
            className={styles.roomTitle}
            data-testid="roomInfo-roomName"
            onClick={onRoomDetailOpen}
          >
            <h4>{roomName}</h4>
          </button>
        )}

        <ItemFeatures
          features={featureGroups}
          roomSize={roomSize}
          capacity={capacity}
          className={styles.itemFeatures}
          isDisplayOtherFeatures
        />
        <p className={styles.description}>{description}</p>
        <div className={styles.roomLinks}>
          <button
            onClick={onRoomDetailOpen}
            className={styles.detailLink}
            data-testid="roomInfo-itemDetailLink"
          >
            <Translate id="Plan_List.Item_Detail.Link" />
          </button>
          <VacancyCalendarEntryButton
            query={query}
            departureAreaCode={departureAreaCode}
            departureDate={query?.[URL_KEYS.DEPARTURE_DATE]}
            returnDate={query?.[URL_KEYS.RETURN_DATE]}
            stayNights={stayNights}
            hotelId={hotelId}
            planId={planId}
            itemId={itemId}
            onSubmitVacancyCalendarFormPath={paths.planDetail.pathResolver(hotelId, planId, itemId)}
            hasMeal={hasMeal}
            roomCapacity={capacity}
            departureStation={departureStation?.name}
            destinationStation={destinationStation?.name}
          />
        </div>
      </div>
      <div className={styles.contentBox}>
        <div className={styles.stationInfo}>
          <div>
            <TextBadge
              text={<Translate id="DP_Common.Train.Route.Title" />}
              className={styles.priceTitle}
            />
            {departureStation && destinationStation && (
              <Translate
                id="DP_Common.Train.Route.Stations"
                data={{
                  departure_station: departureStation?.name,
                  arrival_station: destinationStation?.name,
                }}
                className={styles.stationNames}
              />
            )}
          </div>
          <Translate id="DP_Common.Train.Detail" className={styles.trainDetail} />
        </div>
        <PriceInfo
          price={price}
          adultCount={adultCount}
          numberOfChildren={childCount}
          noOfDays={noOfDays}
          stayNights={stayNights}
          selectButtonText={<Translate id="Plan_List.Detail.Proceed.Train_Select" />}
          points={points}
          roomCount={inventoryCount}
          noOfUnits={noOfUnits}
          className={styles.priceInfo}
          href={trainSearchUrl}
          isPlanPrice
          isBasicTravelFee
        />
      </div>
      {isRoomDetailOpen && (
        <RoomDetailPopUp
          title={<Translate id="Plan_List.Item_Detail.Title" />}
          isOpen={isRoomDetailOpen}
          onClose={onRoomDetailClose}
          item={item}
        />
      )}
    </div>
  );
};

export default RoomInfo;
